// ===========================================
// HOOKS: Tareas (CORE)
// ===========================================

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { tareasService } from '../services/tareasService'
import { lecturasComentariosService, tareasConComentariosSinLeer } from '../services/lecturasComentariosService'
import { notificacionesService } from '../services/notificacionesService'
import { PLANTILLAS_NOTIFICACION, type NotificacionOrigen } from '../types/notificaciones'
import { useAuditor } from '@/hooks/useAuditor'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso, handleMutationError, PermisoError, puedeTocarTarea } from '@/lib/permisos-guard'
import { useNotificacionesTarea } from './useNotificacionesTarea'
import { calendarioKeys } from './useCalendario'
import { rutaAltaDesdeInscripcion } from '../lib/inscripcion'
import { toast } from 'sonner'
import type { Database, EstadoTarea, TareaComentario, TareaCompleta } from '@/types/database'
import type { FiltrosTareas, TareaFormData, TipoVinculado, SubtareaFormData, RecordatorioFormData } from '../types/tareas'

// ===========================================
// Helper: obtener tarea para notificaciones (ORR-54)
// ===========================================
/**
 * Busca una tarea en el cache de listas (tareasKeys.all) con fallback a SELECT.
 *
 * RAZÓN: el query key `tareasKeys.detalle(id)` NO se hidrata en ningún flujo
 * actual del proyecto — nadie llama `useTarea(id)` desde las páginas. Por eso
 * `getQueryData(tareasKeys.detalle(id))` siempre devolvía undefined.
 *
 * Las listas (`tareasKeys.lista(filtros)`) SÍ están hidratadas (useTareas,
 * useMisTareas, etc.), así que buscamos ahí primero. Si no aparece (tarea
 * filtrada o paginada fuera), caemos a un SELECT directo.
 */
export async function obtenerTareaParaNotificacion(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  tareaId: string
): Promise<TareaCompleta | null> {
  // 1. Buscar en todas las listas cacheadas
  const listas = queryClient.getQueriesData<unknown>({ queryKey: tareasKeys.all })
  for (const [, data] of listas) {
    if (!Array.isArray(data)) continue
    const encontrada = (data as TareaCompleta[]).find((t) => t?.id === tareaId)
    if (encontrada) return encontrada
  }

  // 2. Fallback: SELECT directo
  try {
    return await tareasService.obtenerPorId(supabase, tareaId)
  } catch (err) {
    console.error('[notificaciones] No se pudo obtener tarea para notificación:', err)
    return null
  }
}

// ===========================================
// Query Keys
// ===========================================

export const tareasKeys = {
  all: ['tareas'] as const,
  lista: (filtros?: FiltrosTareas) => [...tareasKeys.all, 'lista', filtros] as const,
  detalle: (id: string) => [...tareasKeys.all, 'detalle', id] as const,
  misTareas: (email: string, workspace?: string) =>
    [...tareasKeys.all, 'mis-tareas', email, workspace] as const,
  porCliente: (clienteId: string) => [...tareasKeys.all, 'cliente', clienteId] as const,
  porEntidad: (tipo: TipoVinculado, id: string) =>
    [...tareasKeys.all, 'entidad', tipo, id] as const,
  comentarios: (tareaId: string) => [...tareasKeys.all, 'comentarios', tareaId] as const,
  lecturasComentarios: () => [...tareasKeys.all, 'lecturas-comentarios'] as const,
  subtareas: (tareaId: string) => [...tareasKeys.all, 'subtareas', tareaId] as const,
  recordatorios: (tareaId: string) => [...tareasKeys.all, 'recordatorios', tareaId] as const,
  estadisticas: (workspace?: string) => [...tareasKeys.all, 'estadisticas', workspace] as const,
  asignaciones: (tareaId: string) => [...tareasKeys.all, 'asignaciones', tareaId] as const,
  asignadasPorMi: (email: string, workspace?: string) => [...tareasKeys.all, 'asignadas-por-mi', email, workspace] as const,
}

// Query keys para notificaciones: ver `src/core/hooks/useNotificaciones.ts`.
// Las keys se exportan desde ahí como `notificacionesKeys` (consolidado en ORR-56).

// ===========================================
// Hooks de Query
// ===========================================

/**
 * Hook para obtener todas las tareas con filtros
 * Soporta filtrado por workspace
 */
export function useTareas(filtros?: FiltrosTareas) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.lista(filtros),
    queryFn: () => tareasService.listar(supabase!, filtros),
    enabled: !!supabase && !supabaseLoading,
    refetchInterval: 30 * 1000, // Refrescar cada 30 segundos
  })
}

/**
 * Último comentario de cada tarea, para la columna del listado (ORR-259).
 *
 * Va en una query aparte y no en `vista_tareas_completas` porque la vista ya expone
 * `cantidad_comentarios` y sumarle el texto obligaría a una migración más regenerar
 * `database.ts`. El costo es una request extra por listado.
 */
export function useUltimosComentarios(tareaIds: string[]) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const idsOrdenados = [...tareaIds].sort()

  return useQuery({
    queryKey: [...tareasKeys.all, 'ultimos-comentarios', idsOrdenados],
    queryFn: () => tareasService.ultimosComentarios(supabase!, idsOrdenados),
    enabled: !!supabase && !supabaseLoading && idsOrdenados.length > 0,
    refetchInterval: 30 * 1000,
  })
}

/**
 * Ids de las tareas con un comentario de otra persona que el usuario todavía no leyó.
 * Recibe el último comentario de cada tarea, el que devuelve `useUltimosComentarios`.
 */
export function useComentariosSinLeer(ultimos: Record<string, TareaComentario>): Set<string> {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const ids = Object.keys(ultimos).sort()

  const { data: lecturas } = useQuery({
    queryKey: [...tareasKeys.lecturasComentarios(), ids],
    queryFn: () => lecturasComentariosService.listar(supabase!, ids),
    enabled: !!supabase && !supabaseLoading && ids.length > 0,
  })

  return useMemo(
    () => tareasConComentariosSinLeer(ultimos, lecturas, user?.email),
    [ultimos, lecturas, user?.email]
  )
}

/** Marca leídos los comentarios de una tarea. Es best-effort: un fallo solo se loguea. */
export function useMarcarComentariosLeidos() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (tareaId: string) => lecturasComentariosService.marcarLeidos(supabase!, tareaId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tareasKeys.lecturasComentarios() }),
    onError: (err) => console.error('No se pudieron marcar leídos los comentarios:', err),
  })
}

/**
 * Hook para obtener una tarea por ID
 */
export function useTarea(id: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.detalle(id),
    queryFn: () => tareasService.obtenerPorId(supabase!, id),
    enabled: !!supabase && !supabaseLoading && !!id,
  })
}

/**
 * Hook para obtener las tareas del usuario actual
 */
export function useMisTareas(workspace?: string) {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.misTareas(user?.email || '', workspace),
    queryFn: () => tareasService.obtenerMisTareas(supabase!, user?.email || '', workspace),
    enabled: !!supabase && !supabaseLoading && !!user?.email,
    refetchInterval: 60 * 1000, // Refrescar cada minuto
  })
}

/**
 * Hook para obtener tareas de un cliente
 */
export function useTareasCliente(clienteId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.porCliente(clienteId),
    queryFn: () => tareasService.obtenerPorCliente(supabase!, clienteId),
    enabled: !!supabase && !supabaseLoading && !!clienteId,
  })
}

/**
 * Hook para obtener tareas vinculadas a una entidad (polimórfico)
 */
export function useTareasEntidad(entidadTipo: TipoVinculado, entidadId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.porEntidad(entidadTipo, entidadId),
    queryFn: () => tareasService.obtenerPorEntidad(supabase!, entidadTipo, entidadId),
    enabled: !!supabase && !supabaseLoading && !!entidadTipo && !!entidadId,
  })
}

/**
 * Hook para obtener comentarios de una tarea.
 * Con `abierto` en false no consulta. Al pasar a true consulta de nuevo, porque el cache vence al instante.
 */
export function useComentariosTarea(tareaId: string, abierto = true) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.comentarios(tareaId),
    queryFn: () => tareasService.obtenerComentarios(supabase!, tareaId),
    enabled: !!supabase && !supabaseLoading && !!tareaId && abierto,
    staleTime: 0,
  })
}

/**
 * Hook para obtener estadisticas de tareas
 */
export function useEstadisticasTareas(workspace?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.estadisticas(workspace),
    queryFn: () => tareasService.obtenerEstadisticas(supabase!, workspace),
    enabled: !!supabase && !supabaseLoading,
    refetchInterval: 60 * 1000,
  })
}

// ===========================================
// Hooks de Mutation
// ===========================================

/**
 * Hook para crear una tarea
 */
export function useCrearTarea() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { crear } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: (datos: TareaFormData) => {
      assertPermiso(puede, 'crearTarea')
      if (!organizacionId) throw new Error('No hay organización activa')
      return tareasService.crear(supabase!, {
        ...datos,
        // ORR-381: normalizado en el origen. La RPC compara `created_by` contra el email de
        // usuarios_brainhub, que está en minúsculas, y sin esto una sesión de Auth0 con mayúsculas
        // guardaba una tarea que su propio creador no podía tocar del lado del servidor.
        created_by: (user?.email || '').toLowerCase(),
        organizacion_id: organizacionId,
      })
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      // ORR-228 sumó las tareas al calendario unificado, así que una tarea nueva también
      // invalida ese cache. Sin esto, crearla desde el calendario (ORR-295) la deja
      // invisible hasta que algo más dispare un refetch.
      queryClient.invalidateQueries({ queryKey: calendarioKeys.all })
      // Registrar en auditoria
      crear('tarea', data.id, { titulo: data.titulo, prioridad: data.prioridad })
      // Notificar al asignado (si es diferente al creador)
      if (supabase && data.asignado_a && user?.email && data.asignado_a !== user.email) {
        const plantilla = PLANTILLAS_NOTIFICACION.tareaAsignada(data.titulo, data.fecha_limite)
        const origen: NotificacionOrigen = {
          canal: 'web',
          actor_email: user.email,
          tabla_fuente: 'tareas',
          registro_id: data.id,
        }
        notificacionesService
          .notificar(supabase, {
            destinatarios: [`user:${data.asignado_a}`],
            titulo: plantilla.titulo,
            mensaje: plantilla.mensaje,
            tipo: plantilla.tipo,
            enlace: `/tareas?id=${data.id}`,
            origen,
          })
          .catch((err) => console.error('Error notificación:', err))
      }
    },
    onError: (error) => {
      handleMutationError(error, 'Error al crear la tarea')
    },
  })
}

/**
 * Hook para actualizar una tarea
 * Detecta cambios en asignado, prioridad y fecha_limite para notificar.
 */
export function useActualizarTarea() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()
  const { puede, emailUsuario } = usePermissions()

  return useMutation({
    mutationFn: async ({ id, datos }: { id: string; datos: Partial<TareaFormData> }) => {
      // ORR-200 B3: editarTareaPropia habilita solo tareas propias (asignada a mí o creada por mí)
      if (!puede.editarTarea) {
        assertPermiso(puede, 'editarTareaPropia')
      }
      // Snapshot de la tarea ANTES del UPDATE, para detectar cambios reales (asignado, prioridad,
      // fecha_limite). ORR-381: cuando el rol necesita el guard, ese snapshot se lee de la base en
      // vez del cache — el helper de notificaciones devuelve null si la lectura falla y acepta una
      // copia cacheada con la asignación vieja, y ninguna de las dos cosas sirve para decidir un
      // permiso. Quien tiene `editarTarea` no gatea, así que sigue usando la lectura barata.
      const tareaAntes = puede.editarTarea
        ? await obtenerTareaParaNotificacion(queryClient, supabase!, id)
        : await tareasService.obtenerPorId(supabase!, id)
      if (!puedeTocarTarea(tareaAntes, puede, emailUsuario)) {
        throw new PermisoError('editarTareaPropia')
      }
      const tareaActualizada = await tareasService.actualizar(supabase!, id, datos)
      return { tareaAntes, tareaActualizada }
    },
    onSuccess: ({ tareaAntes, tareaActualizada }, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      // Registrar en auditoria
      editar('tarea', variables.id, { cambios: variables.datos })

      if (!supabase || !user?.email) return
      const userEmail = user.email
      const autor = user.name || user.email
      const enlace = `/tareas?id=${tareaActualizada.id}`
      const nuevoAsignado = tareaActualizada.asignado_a
      const asignadoAnterior = tareaAntes?.asignado_a ?? null

      const origenBase: NotificacionOrigen = {
        canal: 'web',
        actor_email: userEmail,
        tabla_fuente: 'tareas',
        registro_id: tareaActualizada.id,
      }

      // --- Cambio de asignación ---
      if (asignadoAnterior !== nuevoAsignado) {
        // Notificar al saliente (si existía y no soy yo)
        if (asignadoAnterior && asignadoAnterior !== userEmail) {
          const p = PLANTILLAS_NOTIFICACION.tareaReasignadaSaliente(
            tareaActualizada.titulo,
            nuevoAsignado ?? 'nadie',
          )
          notificacionesService
            .notificar(supabase, {
              destinatarios: [`user:${asignadoAnterior}`],
              titulo: p.titulo,
              mensaje: p.mensaje,
              tipo: p.tipo,
              enlace,
              origen: origenBase,
            })
            .catch((err) => console.error('Error notificación:', err))
        }
        // Notificar al nuevo asignado (si existe y no soy yo)
        if (nuevoAsignado && nuevoAsignado !== userEmail) {
          const p = PLANTILLAS_NOTIFICACION.tareaAsignada(
            tareaActualizada.titulo,
            tareaActualizada.fecha_limite,
          )
          notificacionesService
            .notificar(supabase, {
              destinatarios: [`user:${nuevoAsignado}`],
              titulo: p.titulo,
              mensaje: p.mensaje,
              tipo: p.tipo,
              enlace,
              origen: origenBase,
            })
            .catch((err) => console.error('Error notificación:', err))
        }
      }

      // --- Cambio de prioridad ---
      if (
        variables.datos.prioridad !== undefined &&
        tareaAntes?.prioridad !== tareaActualizada.prioridad &&
        nuevoAsignado && nuevoAsignado !== userEmail
      ) {
        const p = PLANTILLAS_NOTIFICACION.tareaCambioPrioridad(
          tareaActualizada.titulo,
          tareaActualizada.prioridad ?? 'media',
          autor,
        )
        notificacionesService
          .notificar(supabase, {
            destinatarios: [`user:${nuevoAsignado}`],
            titulo: p.titulo,
            mensaje: p.mensaje,
            tipo: p.tipo,
            enlace,
            origen: origenBase,
          })
          .catch((err) => console.error('Error notificación:', err))
      }

      // --- Cambio de fecha_limite ---
      if (
        variables.datos.fecha_limite !== undefined &&
        tareaAntes?.fecha_limite !== tareaActualizada.fecha_limite &&
        nuevoAsignado && nuevoAsignado !== userEmail
      ) {
        const p = PLANTILLAS_NOTIFICACION.tareaCambioFechaLimite(
          tareaActualizada.titulo,
          tareaActualizada.fecha_limite,
          autor,
        )
        notificacionesService
          .notificar(supabase, {
            destinatarios: [`user:${nuevoAsignado}`],
            titulo: p.titulo,
            mensaje: p.mensaje,
            tipo: p.tipo,
            enlace,
            origen: origenBase,
          })
          .catch((err) => console.error('Error notificación:', err))
      }
    },
    onError: (error) => {
      // ORR-381: el guard nuevo puede rechazar, y antes la lectura previa nunca fallaba. Sin
      // esto el formulario queda abierto y el motivo solo aparece en la consola.
      handleMutationError(error, 'Error al actualizar la tarea')
    },
  })
}

/**
 * Hook para cambiar el estado de una tarea
 * Usa optimistic updates para mejor UX en drag & drop
 *
 * Al completar una inscripción sin cliente, lleva al alta del cliente con el CUIT precargado. Sin
 * permiso para crear clientes, avisa y no navega. Con `enLote` no hace ninguna de las dos cosas.
 */
export function useCambiarEstadoTarea() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { cambiarEstado } = useAuditor()
  const { puede, emailUsuario } = usePermissions()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async ({
      id,
      estado,
      estadoAnterior,
    }: {
      id: string
      estado: EstadoTarea
      estadoAnterior?: EstadoTarea
      /** El cambio es parte de una selección múltiple. */
      enLote?: boolean
    }) => {
      // ORR-381: el gate vive acá y no en cada caller. Cuatro superficies llaman a este hook y
      // cada una tenía su propia regla, así que un asistente podía mover una tarea ajena desde el
      // tablero y no desde la ficha del cliente.
      if (!puede.editarTarea) {
        assertPermiso(puede, 'editarTareaPropia')
        // Se lee de la base, no del cache: es un guard de permisos y una copia cacheada puede
        // traer una asignación vieja. Si la lectura falla, el error propaga y `puedeTocarTarea`
        // nunca llega a decidir con datos que no pudo verificar.
        const tarea = await tareasService.obtenerPorId(supabase!, id)
        if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
          throw new PermisoError('editarTareaPropia')
        }
      }
      const result = await tareasService.cambiarEstado(supabase!, id, estado)
      return { ...result, estadoAnterior }
    },
    // Optimistic update: actualizar UI inmediatamente
    onMutate: async (variables) => {
      // Cancelar queries pendientes para evitar overwrites
      await queryClient.cancelQueries({ queryKey: tareasKeys.all })

      // Snapshot del estado anterior
      const previousTareas = queryClient.getQueriesData({ queryKey: tareasKeys.all })

      // Actualizar optimisticamente todas las queries de tareas
      // Actualizar todas las listas (independiente de filtros) para que el cambio
      // se vea inmediatamente y evitar el rebote al soltar una tarjeta
      queryClient.setQueriesData(
        { queryKey: ['tareas', 'lista'] },
        (old: unknown) => {
          if (!Array.isArray(old)) return old
          return old.map((tarea: { id: string; estado: EstadoTarea }) =>
            tarea.id === variables.id
              ? { ...tarea, estado: variables.estado }
              : tarea
          )
        }
      )

      return { previousTareas }
    },
    onError: (_err, _variables, context) => {
      // Rollback en caso de error
      if (context?.previousTareas) {
        context.previousTareas.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSuccess: (data, variables) => {
      if (variables.estado !== 'completada' || variables.enLote) return
      if (data.tipo !== 'inscripcion' || data.cliente_id) return
      if (!puede.crearCliente) {
        toast.info('El cliente de la inscripción no se puede dar de alta porque tu rol no puede crear clientes.')
        return
      }
      navigate(rutaAltaDesdeInscripcion(data.id))
    },
    onSettled: (data, _error, variables) => {
      // Refrescar datos del servidor después de completar
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })

      // ORR-381: la auditoría va DESPUÉS de la guarda de error. Antes se escribía siempre, así que
      // un cambio rechazado por permisos dejaba registrado un cambio de estado que nunca ocurrió.
      if (!data || _error || !supabase || !user?.email) return

      // Registrar en auditoria
      if (variables.estadoAnterior) {
        cambiarEstado(
          'tarea',
          variables.id,
          variables.estadoAnterior,
          variables.estado
        )
      }

      const userEmail = user.email
      const autor = user.name || user.email
      const asignado = data.asignado_a ?? null
      const asignadoPor = data.asignado_por ?? null
      const enlace = `/tareas?id=${data.id}`
      const origenBase: NotificacionOrigen = {
        canal: 'web',
        actor_email: userEmail,
        tabla_fuente: 'tareas',
        registro_id: data.id,
      }

      // --- Tarea completada: notificar al asignador ---
      if (variables.estado === 'completada' && asignadoPor && asignadoPor !== userEmail) {
        const p = PLANTILLAS_NOTIFICACION.tareaCompletada(data.titulo, autor)
        notificacionesService
          .notificar(supabase, {
            destinatarios: [`user:${asignadoPor}`],
            titulo: p.titulo,
            mensaje: p.mensaje,
            tipo: p.tipo,
            enlace,
            origen: origenBase,
          })
          .catch((err) => console.error('Error notificación:', err))
      }

      // --- Cambio de estado no-completada: notificar al otro lado ---
      if (variables.estado !== 'completada') {
        const p = PLANTILLAS_NOTIFICACION.tareaCambioEstado(
          data.titulo,
          variables.estado,
          autor,
        )
        // Si el actor es supervisor moviendo la tarea, avisar al asignado
        if (asignado && asignado !== userEmail) {
          notificacionesService
            .notificar(supabase, {
              destinatarios: [`user:${asignado}`],
              titulo: p.titulo,
              mensaje: p.mensaje,
              tipo: p.tipo,
              enlace,
              origen: origenBase,
            })
            .catch((err) => console.error('Error notificación:', err))
        }
        // Si el actor es el asignado, avisar al supervisor (asignador)
        if (
          asignado === userEmail &&
          asignadoPor && asignadoPor !== userEmail
        ) {
          notificacionesService
            .notificar(supabase, {
              destinatarios: [`user:${asignadoPor}`],
              titulo: p.titulo,
              mensaje: p.mensaje,
              tipo: p.tipo,
              enlace,
              origen: origenBase,
            })
            .catch((err) => console.error('Error notificación:', err))
        }
      }
    },
  })
}

/**
 * Hook para guardar el orden de una columna del tablero (drag & drop).
 * Actualiza la posicion en la caché antes de que responda el servidor.
 */
export function useReordenarTareas() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({ tareaId, orden }: { tareaId: string; orden: string[] }) =>
      tareasService.reordenar(supabase!, tareaId, orden),
    onMutate: async ({ orden }) => {
      await queryClient.cancelQueries({ queryKey: tareasKeys.all })
      const previousTareas = queryClient.getQueriesData({ queryKey: tareasKeys.all })
      const posicionPorId = new Map(orden.map((id, indice) => [id, indice]))

      // Actualizar todas las listas (independiente de filtros) para que el reordenamiento
      // se refleje de inmediato sin esperar el refetch
      queryClient.setQueriesData(
        { queryKey: ['tareas', 'lista'] },
        (old: unknown) => {
          if (!Array.isArray(old)) return old
          return old.map((tarea: { id: string; posicion: number }) =>
            posicionPorId.has(tarea.id)
              ? { ...tarea, posicion: posicionPorId.get(tarea.id)! }
              : tarea
          )
        }
      )

      return { previousTareas }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousTareas) {
        context.previousTareas.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
    },
  })
}

/**
 * Hook para eliminar una tarea
 * Notifica al asignado (si existe y es diferente al actor).
 */
export function useEliminarTarea() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { eliminar } = useAuditor()
  const { puede } = usePermissions()

  return useMutation({
    mutationFn: async (id: string) => {
      assertPermiso(puede, 'eliminarTarea')
      // Obtener datos de la tarea antes de eliminar para el log
      const tarea = await tareasService.obtenerPorId(supabase!, id)
      await tareasService.eliminar(supabase!, id)
      return tarea
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      // Registrar en auditoria
      eliminar('tarea', data.id, { titulo: data.titulo })

      // Notificar al asignado (si existía y es diferente al que elimina)
      if (supabase && user?.email && data.asignado_a && data.asignado_a !== user.email) {
        const autor = user.name || user.email
        const p = PLANTILLAS_NOTIFICACION.tareaEliminada(data.titulo, autor)
        notificacionesService
          .notificar(supabase, {
            destinatarios: [`user:${data.asignado_a}`],
            titulo: p.titulo,
            mensaje: p.mensaje,
            tipo: p.tipo,
            origen: {
              canal: 'web',
              actor_email: user.email,
              tabla_fuente: 'tareas',
              registro_id: data.id,
            },
          })
          .catch((err) => console.error('Error notificación:', err))
      }
    },
  })
}

/**
 * Hook para vincular tarea a una entidad
 */
export function useVincularTarea() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()

  return useMutation({
    mutationFn: ({
      tareaId,
      entidadTipo,
      entidadId,
    }: {
      tareaId: string
      entidadTipo: TipoVinculado
      entidadId: string
    }) => tareasService.vincular(supabase!, tareaId, entidadTipo, entidadId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      editar('tarea', variables.tareaId, {
        vinculado_tipo: variables.entidadTipo,
        vinculado_id: variables.entidadId,
      })
    },
  })
}

/**
 * Hook para desvincular tarea de una entidad
 */
export function useDesvincularTarea() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { editar } = useAuditor()

  return useMutation({
    mutationFn: (tareaId: string) => tareasService.desvincular(supabase!, tareaId),
    onSuccess: (_, tareaId) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      editar('tarea', tareaId, { vinculado_tipo: null, vinculado_id: null })
    },
  })
}

/**
 * Hook para agregar un comentario a una tarea
 */
export function useAgregarComentario() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { notificarActividad } = useNotificacionesTarea()

  return useMutation({
    mutationFn: ({ tareaId, contenido }: { tareaId: string; contenido: string }) =>
      tareasService.agregarComentario(supabase!, tareaId, contenido, user?.email || ''),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.comentarios(variables.tareaId) })
      queryClient.invalidateQueries({ queryKey: tareasKeys.detalle(variables.tareaId) })

      // Notificar al asignado y/o supervisor (busca en listas cacheadas + SELECT fallback)
      if (!supabase) return
      const tarea = await obtenerTareaParaNotificacion(queryClient, supabase, variables.tareaId)
      if (tarea) {
        notificarActividad(tarea, { tipo: 'comentario' })
      }
    },
  })
}

/**
 * Hook para eliminar un comentario
 */
export function useEliminarComentario() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({ id, tareaId: _ }: { id: string; tareaId: string }) =>
      tareasService.eliminarComentario(supabase!, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.comentarios(variables.tareaId) })
      queryClient.invalidateQueries({ queryKey: tareasKeys.detalle(variables.tareaId) })
    },
  })
}

// ===========================================
// SUBTAREAS (CHECKLIST)
// ===========================================

/**
 * Hook para obtener subtareas de una tarea
 */
export function useSubtareas(tareaId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.subtareas(tareaId),
    queryFn: () => tareasService.obtenerSubtareas(supabase!, tareaId),
    enabled: !!supabase && !supabaseLoading && !!tareaId,
  })
}

/**
 * Hook para crear una subtarea
 */
export function useCrearSubtarea() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { notificarActividad } = useNotificacionesTarea()
  const { puede, emailUsuario } = usePermissions()

  return useMutation({
    mutationFn: async ({ tareaId, datos }: { tareaId: string; datos: SubtareaFormData }) => {
      // ORR-386: la subtarea no tiene dueño propio, hereda el permiso de la tarea padre. Mismo
      // gate que useCambiarEstadoTarea (ORR-381), vía la única regla compartida.
      if (!puede.editarTarea) {
        assertPermiso(puede, 'editarTareaPropia')
        const tarea = await tareasService.obtenerPorId(supabase!, tareaId)
        if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
          throw new PermisoError('editarTareaPropia')
        }
      }
      return tareasService.crearSubtarea(supabase!, tareaId, datos)
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.subtareas(variables.tareaId) })

      // Notificar al asignado y/o supervisor
      if (!supabase) return
      const tarea = await obtenerTareaParaNotificacion(queryClient, supabase, variables.tareaId)
      if (tarea) {
        notificarActividad(tarea, { tipo: 'subtarea', tituloSubtarea: variables.datos.titulo })
      }
    },
  })
}

/**
 * Hook para actualizar una subtarea
 */
export function useActualizarSubtarea() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede, emailUsuario } = usePermissions()

  return useMutation({
    mutationFn: async ({ id, tareaId, datos }: { id: string; tareaId: string; datos: Partial<SubtareaFormData> }) => {
      // ORR-386: ver useCrearSubtarea.
      if (!puede.editarTarea) {
        assertPermiso(puede, 'editarTareaPropia')
        const tarea = await tareasService.obtenerPorId(supabase!, tareaId)
        if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
          throw new PermisoError('editarTareaPropia')
        }
      }
      return tareasService.actualizarSubtarea(supabase!, id, datos)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.subtareas(variables.tareaId) })
    },
  })
}

/**
 * Hook para toggle completada de subtarea
 */
export function useToggleSubtarea() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede, emailUsuario } = usePermissions()

  return useMutation({
    mutationFn: async ({ id, tareaId, completada }: { id: string; tareaId: string; completada: boolean }) => {
      // ORR-386: ver useCrearSubtarea. El optimistic update de abajo pinta el toggle ANTES de
      // este chequeo; si el guard rechaza, el rollback de onError lo revierte (igual que
      // useCambiarEstadoTarea).
      if (!puede.editarTarea) {
        assertPermiso(puede, 'editarTareaPropia')
        const tarea = await tareasService.obtenerPorId(supabase!, tareaId)
        if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
          throw new PermisoError('editarTareaPropia')
        }
      }
      return tareasService.toggleSubtarea(supabase!, id, completada)
    },
    // Optimistic update para UX fluida
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: tareasKeys.subtareas(variables.tareaId) })

      const previousSubtareas = queryClient.getQueryData(tareasKeys.subtareas(variables.tareaId))

      queryClient.setQueryData(
        tareasKeys.subtareas(variables.tareaId),
        (old: unknown) => {
          if (!Array.isArray(old)) return old
          return old.map((subtarea: { id: string; completada: boolean }) =>
            subtarea.id === variables.id
              ? { ...subtarea, completada: variables.completada }
              : subtarea
          )
        }
      )

      return { previousSubtareas }
    },
    onError: (_err, variables, context) => {
      if (context?.previousSubtareas) {
        queryClient.setQueryData(tareasKeys.subtareas(variables.tareaId), context.previousSubtareas)
      }
    },
    onSettled: (_, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.subtareas(variables.tareaId) })
    },
  })
}

/**
 * Hook para eliminar una subtarea
 */
export function useEliminarSubtarea() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede, emailUsuario } = usePermissions()

  return useMutation({
    mutationFn: async ({ id, tareaId }: { id: string; tareaId: string }) => {
      // ORR-386: ver useCrearSubtarea.
      if (!puede.editarTarea) {
        assertPermiso(puede, 'editarTareaPropia')
        const tarea = await tareasService.obtenerPorId(supabase!, tareaId)
        if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
          throw new PermisoError('editarTareaPropia')
        }
      }
      return tareasService.eliminarSubtarea(supabase!, id)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.subtareas(variables.tareaId) })
    },
  })
}

/**
 * Hook para reordenar subtareas
 */
export function useReordenarSubtareas() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { puede, emailUsuario } = usePermissions()

  return useMutation({
    mutationFn: async ({ tareaId, subtareas }: { tareaId: string; subtareas: { id: string; posicion: number }[] }) => {
      // ORR-386: ver useCrearSubtarea. Hoy sin caller en la UI (ver SubtareasChecklist, el grip
      // de arrastre es decorativo); se gatea igual porque el ticket la nombra explícitamente.
      if (!puede.editarTarea) {
        assertPermiso(puede, 'editarTareaPropia')
        const tarea = await tareasService.obtenerPorId(supabase!, tareaId)
        if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
          throw new PermisoError('editarTareaPropia')
        }
      }
      return tareasService.reordenarSubtareas(supabase!, subtareas)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: tareasKeys.subtareas(variables.tareaId) })

      const previousSubtareas = queryClient.getQueryData(tareasKeys.subtareas(variables.tareaId))

      queryClient.setQueryData(
        tareasKeys.subtareas(variables.tareaId),
        (old: unknown) => {
          if (!Array.isArray(old)) return old
          return [...old].sort((a, b) => {
            const posA = variables.subtareas.find(s => s.id === a.id)?.posicion ?? a.posicion
            const posB = variables.subtareas.find(s => s.id === b.id)?.posicion ?? b.posicion
            return posA - posB
          })
        }
      )

      return { previousSubtareas }
    },
    onError: (_err, variables, context) => {
      if (context?.previousSubtareas) {
        queryClient.setQueryData(tareasKeys.subtareas(variables.tareaId), context.previousSubtareas)
      }
    },
    onSettled: (_, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.subtareas(variables.tareaId) })
    },
  })
}

// ===========================================
// RECORDATORIOS
// ===========================================

/**
 * Hook para obtener recordatorios de una tarea
 */
export function useRecordatorios(tareaId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: tareasKeys.recordatorios(tareaId),
    queryFn: () => tareasService.obtenerRecordatorios(supabase!, tareaId),
    enabled: !!supabase && !supabaseLoading && !!tareaId,
  })
}

/**
 * Hook para crear un recordatorio
 */
export function useCrearRecordatorio() {
  const { user } = useAuth0()
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { notificarActividad } = useNotificacionesTarea()

  return useMutation({
    mutationFn: ({ tareaId, datos }: { tareaId: string; datos: RecordatorioFormData }) => {
      const usuarioEmail = user?.email
      if (!usuarioEmail) {
        throw new Error('No se encontró el email del usuario autenticado')
      }
      return tareasService.crearRecordatorio(supabase!, tareaId, usuarioEmail, datos)
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.recordatorios(variables.tareaId) })

      // Notificar al asignado y/o supervisor
      if (!supabase) return
      const tarea = await obtenerTareaParaNotificacion(queryClient, supabase, variables.tareaId)
      if (tarea) {
        notificarActividad(tarea, { tipo: 'recordatorio' })
      }
    },
  })
}

/**
 * Hook para eliminar un recordatorio
 */
export function useEliminarRecordatorio() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({ id, tareaId: _tareaId }: { id: string; tareaId: string }) =>
      tareasService.eliminarRecordatorio(supabase!, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.recordatorios(variables.tareaId) })
    },
  })
}

// ===========================================
// NOTIFICACIONES
// ===========================================
// Los hooks de notificaciones in-app viven en `src/core/hooks/useNotificaciones.ts`.
// Usar desde ahí:
//   - useNotificaciones, useNotificacionesNoLeidas, useConteoNotificaciones,
//     useResumenNotificaciones, useCrearNotificacion, useMarcarLeida,
//     useMarcarTodasLeidas, useEliminarNotificacion, useLimpiarNotificacionesAntiguas,
//     useNotificador
// Consolidado en ORR-56 (Epic ORR-55 Fase 1).

// ===========================================
// Delegación de tareas
// ===========================================

export function useAsignarTarea() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { editar } = useAuditor()

  return useMutation({
    // C6 FIX: mutationFn solo hace el RPC (atómico)
    mutationFn: async (params: { tareaId: string; asignadoA: string; organizacionId: string; tituloTarea: string; motivo?: string }) => {
      // Snapshot de la tarea antes del RPC para detectar al asignado anterior
      const tareaAntes = queryClient.getQueryData<{
        id: string
        asignado_a: string | null
        fecha_limite: string | null
      }>(tareasKeys.detalle(params.tareaId))
      await tareasService.asignarTarea(supabase!, params.tareaId, params.asignadoA, user!.email!, params.organizacionId, params.motivo)
      return { asignadoAnterior: tareaAntes?.asignado_a ?? null, fechaLimite: tareaAntes?.fecha_limite ?? null }
    },
    // M1 FIX: side effects en onSuccess (solo si RPC exitoso)
    onSuccess: async (result, params) => {
      queryClient.invalidateQueries({ queryKey: tareasKeys.all })
      toast.success('Tarea asignada exitosamente')
      editar('tarea', params.tareaId, { delegado_a: params.asignadoA })

      const userEmail = user!.email!
      const enlace = `/tareas?id=${params.tareaId}`
      const origenBase: NotificacionOrigen = {
        canal: 'web',
        actor_email: userEmail,
        tabla_fuente: 'tareas',
        registro_id: params.tareaId,
      }

      // ORR-69: el trigger pg_net enviar_push_async ya dispara push del SO
      // automaticamente al insertar en notificaciones. El fetch manual a
      // push-send aqui (legacy pre-trigger) producia push duplicado, por
      // eso se elimino.
      if (params.asignadoA !== userEmail) {
        const p = PLANTILLAS_NOTIFICACION.tareaAsignada(params.tituloTarea, result.fechaLimite)
        notificacionesService
          .notificar(supabase!, {
            destinatarios: [`user:${params.asignadoA}`],
            titulo: p.titulo,
            mensaje: p.mensaje,
            tipo: p.tipo,
            enlace,
            origen: origenBase,
          })
          .catch(() => {})
      }

      // Notificar al asignado anterior (reasignación saliente)
      if (
        result.asignadoAnterior &&
        result.asignadoAnterior !== userEmail &&
        result.asignadoAnterior !== params.asignadoA
      ) {
        const p = PLANTILLAS_NOTIFICACION.tareaReasignadaSaliente(
          params.tituloTarea,
          params.asignadoA,
        )
        notificacionesService
          .notificar(supabase!, {
            destinatarios: [`user:${result.asignadoAnterior}`],
            titulo: p.titulo,
            mensaje: p.mensaje,
            tipo: p.tipo,
            enlace,
            origen: origenBase,
          })
          .catch(() => {})
      }
    },
    onError: (error: Error) => {
      console.error('Error asignando tarea:', error)
      const msg = error.message?.includes('No tiene permisos') ? 'No tenés permisos para delegar tareas' : error.message?.includes('no pertenece') ? 'El usuario no pertenece a esta organización' : 'Error al asignar la tarea'
      toast.error(msg)
    },
  })
}

export function useHistorialAsignaciones(tareaId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  return useQuery({
    queryKey: tareasKeys.asignaciones(tareaId),
    queryFn: () => tareasService.obtenerHistorialAsignaciones(supabase!, tareaId),
    enabled: !!supabase && !supabaseLoading && !!tareaId,
  })
}

export function useTareasAsignadasPorMi(workspace?: string) {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  return useQuery({
    queryKey: tareasKeys.asignadasPorMi(user?.email ?? '', workspace),
    queryFn: () => tareasService.obtenerTareasAsignadasPor(supabase!, user!.email!, workspace),
    enabled: !!supabase && !supabaseLoading && !!user?.email,
  })
}

// ===========================================
// Re-exports
// ===========================================

export type {
  FiltrosTareas,
  TareaFormData,
  TipoVinculado,
  TareaSubtarea,
  SubtareaFormData,
  TareaRecordatorio,
  RecordatorioFormData,
  Notificacion,
  TareaAsignacion,
} from '../types/tareas'
export { ESTADO_TAREA_CONFIG, PRIORIDAD_CONFIG } from '../types/tareas'
